import { useState, useEffect, useCallback } from "react";
import { Plus, Mail, MessageSquare, Megaphone, Trash2 } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { PageHeader, Loading, Empty, Pill, SectionCard } from "../components/kit";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { FormModal, Field, Input, Textarea, Select } from "../components/form";
import { titleCase } from "../lib/constants";
import { toast } from "sonner";

export default function Marketing() {
  return (
    <div className="animate-fade-up">
      <PageHeader title="Marketing" subtitle="Templates, campaigns, and drip sequences to nurture every segment." testid="marketing-header" />
      <Tabs defaultValue="templates">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="templates" className="data-[state=active]:bg-navy-800 data-[state=active]:text-white">Templates</TabsTrigger>
          <TabsTrigger value="campaigns" className="data-[state=active]:bg-navy-800 data-[state=active]:text-white">Campaigns</TabsTrigger>
        </TabsList>
        <TabsContent value="templates" className="mt-5"><Templates /></TabsContent>
        <TabsContent value="campaigns" className="mt-5"><Campaigns /></TabsContent>
      </Tabs>
    </div>
  );
}

function Templates() {
  const [items, setItems] = useState(null);
  const [modal, setModal] = useState(false);
  const [f, setF] = useState({});
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => { const { data } = await api.get("/templates?limit=200"); setItems(data.items); }, []);
  useEffect(() => { load(); }, [load]);
  const open = () => { setF({ channel: "email", category: "FSBO Follow-Up" }); setModal(true); };
  const submit = async () => { setLoading(true); try { await api.post("/templates", f); toast.success("Template saved"); setModal(false); load(); } catch (e) { toast.error(apiErr(e)); } setLoading(false); };
  const del = async (id) => { await api.delete(`/templates/${id}`); load(); };

  if (!items) return <Loading />;
  return (
    <div>
      <div className="flex justify-end mb-4"><Button className="bg-navy-800 text-white" onClick={open} data-testid="new-template-btn"><Plus className="w-4 h-4 mr-1.5" />New Template</Button></div>
      {items.length === 0 ? <Empty title="No templates" subtitle="Create reusable email and SMS templates." /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((t) => (
            <div key={t.id} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">{t.channel === "email" ? <Mail className="w-4 h-4 text-violet-600" /> : <MessageSquare className="w-4 h-4 text-emerald-600" />}<p className="font-display font-semibold text-navy-800">{t.name}</p></div>
                <button onClick={() => del(t.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
              <Pill className="mt-2">{t.category}</Pill>
              {t.subject && <p className="text-sm font-medium text-slate-600 mt-2">{t.subject}</p>}
              <p className="text-sm text-slate-500 mt-1 line-clamp-3">{t.body}</p>
            </div>
          ))}
        </div>
      )}
      <FormModal open={modal} onClose={() => setModal(false)} title="New Template" onSubmit={submit} loading={loading} testid="template-modal">
        <Field label="Name" required><Input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} required /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Channel"><Select options={["email", "sms"]} value={f.channel} onChange={(v) => setF({ ...f, channel: v })} /></Field>
          <Field label="Category"><Select options={["FSBO Follow-Up", "Past Client", "Buyer Nurture", "Seller Nurture", "Investor", "Referral", "Market Update"]} value={f.category} onChange={(v) => setF({ ...f, category: v })} /></Field>
        </div>
        {f.channel === "email" && <Field label="Subject"><Input value={f.subject || ""} onChange={(e) => setF({ ...f, subject: e.target.value })} /></Field>}
        <Field label="Body" required><Textarea value={f.body || ""} onChange={(e) => setF({ ...f, body: e.target.value })} required className="min-h-[120px]" /></Field>
      </FormModal>
    </div>
  );
}

function Campaigns() {
  const [items, setItems] = useState(null);
  const [modal, setModal] = useState(false);
  const [f, setF] = useState({});
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => { const { data } = await api.get("/campaigns?limit=200"); setItems(data.items); }, []);
  useEffect(() => { load(); }, [load]);
  const submit = async () => { setLoading(true); try { await api.post("/campaigns", { ...f, status: "active", sent: 0, opened: 0 }); toast.success("Campaign created"); setModal(false); load(); } catch (e) { toast.error(apiErr(e)); } setLoading(false); };

  if (!items) return <Loading />;
  return (
    <div>
      <div className="flex justify-end mb-4"><Button className="bg-navy-800 text-white" onClick={() => { setF({ type: "email" }); setModal(true); }} data-testid="new-campaign-btn"><Plus className="w-4 h-4 mr-1.5" />New Campaign</Button></div>
      {items.length === 0 ? <Empty title="No campaigns" subtitle="Launch nurture campaigns for your segments." /> : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100">
          {items.map((c) => (
            <div key={c.id} className="flex items-center gap-3 px-5 py-4">
              <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center"><Megaphone className="w-5 h-5 text-gold-dark" /></div>
              <div className="flex-1"><p className="text-sm font-medium text-navy-800">{c.name}</p><p className="text-xs text-slate-400">{titleCase(c.type)} · {c.segment || "All contacts"}</p></div>
              <div className="text-right text-xs text-slate-500"><p>Sent: {c.sent || 0} · Opened: {c.opened || 0}</p><Pill className="mt-1">{c.status}</Pill></div>
            </div>
          ))}
        </div>
      )}
      <FormModal open={modal} onClose={() => setModal(false)} title="New Campaign" onSubmit={submit} loading={loading} testid="campaign-modal">
        <Field label="Name" required><Input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} required /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type"><Select options={["email", "sms", "drip"]} value={f.type} onChange={(v) => setF({ ...f, type: v })} /></Field>
          <Field label="Segment"><Input value={f.segment || ""} onChange={(e) => setF({ ...f, segment: e.target.value })} placeholder="FSBO, Past Clients…" /></Field>
        </div>
      </FormModal>
    </div>
  );
}
